import { alert } from '@/services/alert';
import { useIVRConfig } from './hooks/useIVRConfig';
import { Attachment } from '@medplum/fhirtypes';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Link, useParams } from 'react-router';
import IVRKeyMappingRow from './components/IVRKeyMappingRow';
import { IVRRouteTester } from './components/IVRRouteTester';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { IvrDigit, IvrKeyMapping, IvrTemplate, prepareKeyMappingForSave } from '@bariendo/lib/ivr';
import { IVR_TEMPLATE_DISPLAY_NAMES, isActiveIvrTemplateId } from '@bariendo/lib/constants';
import { IvrAudioFileInput } from './components/IvrAudioFileInput';
import { useIvrAudioUpload } from './components/ivrAudio';
import { applyEndOfChainAudio, findEndOfChainAudio, findIncompleteRungDigit } from './components/ivrRungs';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Checkbox,
  Container,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

export function IVRTemplateEditorPage(): ReactElement {
  const {
    currentUserPermissions: { canConfigureIvr },
  } = useCurrentUser();

  // BAR-2441: gate before the editor mounts. Previously an unauthorized user got
  // the full editable form with a warning banner above it, and every save 403'd.
  // ProjectMembership.admin is deliberately not accepted here — it grants project
  // administration, not a policy that permits writing Basic.
  if (!canConfigureIvr) {
    return (
      <Container size="lg" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          This page is only accessible to administrators and users granted IVR configuration access.
        </Alert>
      </Container>
    );
  }

  return <AuthorizedIVRTemplateEditorPage />;
}

function AuthorizedIVRTemplateEditorPage(): ReactElement {
  const { templateId: templateIdParam } = useParams();
  const { loading, languageSwitchKey, hoursSets, templateEntries, saveTemplate } = useIVRConfig();

  // Only reachable templates are editable; an off-hours id in the URL falls through to
  // the invalid-template branch rather than opening an editor nothing will play.
  const templateId = isActiveIvrTemplateId(templateIdParam) ? templateIdParam : undefined;

  const existingEntry = templateId ? templateEntries.get(templateId) : undefined;

  const [name, setName] = useState('');
  const [description, setDescription] = useState<string | undefined>();
  const [audioFile, setAudioFile] = useState<Attachment | undefined>();
  const [invalidAudioFile, setInvalidAudioFile] = useState<Attachment | undefined>();
  const [keyMappings, setKeyMappings] = useState<IvrKeyMapping[]>([]);
  const [endOfChainAudio, setEndOfChainAudio] = useState<Attachment | undefined>();
  const [playGoodbyeMessage, setPlayGoodbyeMessage] = useState(false);
  const [saving, setSaving] = useState(false);

  const menuAudio = useIvrAudioUpload(setAudioFile);
  const invalidAudio = useIvrAudioUpload(setInvalidAudioFile);
  const goodbyeAudio = useIvrAudioUpload(setEndOfChainAudio);
  const uploading = menuAudio.uploading || invalidAudio.uploading || goodbyeAudio.uploading;

  useEffect(() => {
    if (!existingEntry) {
      return;
    }

    const { parsed } = existingEntry;
    const goodbye = findEndOfChainAudio(parsed.keyMappings);

    setName(parsed.name);
    setDescription(parsed.description);
    setAudioFile(parsed.audioFile);
    setInvalidAudioFile(parsed.invalidAudioFile);
    setKeyMappings(parsed.keyMappings);
    setEndOfChainAudio(goodbye);
    setPlayGoodbyeMessage(!!goodbye);
  }, [existingEntry]);

  const handleKeyMappingChange = useCallback((digit: IvrKeyMapping['digit'], mapping: IvrKeyMapping | undefined) => {
    setKeyMappings((current) => {
      const withoutDigit = current.filter((item) => item.digit !== digit);
      return !mapping ? withoutDigit : [...withoutDigit, mapping].sort((a, b) => a.digit - b.digit);
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!templateId || !name.trim()) {
      alert.error('Template name is required.');
      return;
    }

    // prepareKeyMappingForSave drops an unusable rung, which is right for a chain left
    // half-edited but wrong as the only feedback: the option would save one rung shorter than
    // what is on screen. Name the digit and let the admin finish it.
    const incompleteDigit = findIncompleteRungDigit(keyMappings);

    if (incompleteDigit !== undefined) {
      alert.error(`Digit ${incompleteDigit} has a rung with nothing to dial. Fill it in or remove it before saving.`);
      return;
    }

    const template: IvrTemplate = {
      templateId,
      audioFile,
      name: name.trim(),
      invalidAudioFile,
      description: description?.trim() || undefined,
      keyMappings: applyEndOfChainAudio(keyMappings, playGoodbyeMessage ? endOfChainAudio : undefined)
        .map((keyMapping) => prepareKeyMappingForSave(keyMapping, languageSwitchKey))
        .filter((keyMapping): keyMapping is IvrKeyMapping => !!keyMapping),
    };

    try {
      setSaving(true);
      await saveTemplate(templateId, template);
      alert.success('IVR template saved.');
    } catch (error) {
      alert.error(`Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  }, [
    audioFile,
    description,
    endOfChainAudio,
    invalidAudioFile,
    keyMappings,
    languageSwitchKey,
    name,
    playGoodbyeMessage,
    saveTemplate,
    templateId,
  ]);

  const hoursSetList = useMemo(() => [...hoursSets.values()], [hoursSets]);

  const pageTitle = templateId ? IVR_TEMPLATE_DISPLAY_NAMES[templateId] : 'IVR Template';

  if (!templateId) {
    return (
      <Container size="lg" py="md">
        <Text c="dimmed">Invalid template ID.</Text>
        <Anchor component={Link} to="/ivr-configuration" mt="md">
          Back to IVR configuration
        </Anchor>
      </Container>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!existingEntry) {
    return (
      <Container size="lg" py="md">
        <Text c="dimmed">Template not found.</Text>
        <Anchor component={Link} to="/ivr-configuration" mt="md">
          Back to IVR configuration
        </Anchor>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Anchor component={Link} to="/ivr-configuration" size="sm">
              <Group gap={4}>
                <IconArrowLeft size={14} />
                Back to IVR configuration
              </Group>
            </Anchor>
            <Title order={1}>{pageTitle}</Title>
          </Stack>
        </Group>

        <ScrollArea viewportProps={{ style: { maxHeight: 'calc(100vh - 270px)' } }}>
          <Group gap="md">
            <IvrAudioFileInput
              label="Menu audio file"
              audioFile={audioFile}
              disabled={false}
              uploading={menuAudio.uploading}
              uploadPromptSuffix="the full menu prompt audio"
              emptyDescription="Upload the full menu prompt audio"
              onFileChange={menuAudio.handleFileChange}
              onRemove={() => setAudioFile(undefined)}
            />

            <IvrAudioFileInput
              label="Invalid input audio file"
              audioFile={invalidAudioFile}
              disabled={false}
              uploading={invalidAudio.uploading}
              uploadPromptSuffix="the invalid input audio file"
              emptyDescription="Upload audio played when caller presses an unrecognized key"
              onFileChange={invalidAudio.handleFileChange}
              onRemove={() => setInvalidAudioFile(undefined)}
            />
          </Group>

          <Stack gap="sm" mt="md">
            <Title order={3}>Key mappings</Title>

            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={70}>Digit</Table.Th>
                  <Table.Th w={150}>Hours</Table.Th>
                  <Table.Th>In hours</Table.Th>
                  <Table.Th>After hours</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Array.from({ length: 10 }, (_, index) => index as IvrDigit).map((digit) => (
                  <IVRKeyMappingRow
                    key={digit}
                    digit={digit}
                    disabled={false}
                    hoursSets={hoursSetList}
                    onChange={handleKeyMappingChange}
                    languageSwitchKey={languageSwitchKey}
                    mapping={keyMappings.find((mapping) => mapping.digit === digit)}
                  />
                ))}
              </Table.Tbody>
            </Table>

            <Paper withBorder px="md" py="sm">
              <Group gap="md" align="flex-end" wrap="wrap">
                <Text size="sm" fw={600}>
                  When the chain runs out
                </Text>
                <Checkbox
                  size="sm"
                  checked={playGoodbyeMessage}
                  label="Play a goodbye message before hanging up"
                  onChange={(event) => setPlayGoodbyeMessage(event.currentTarget.checked)}
                />
                {playGoodbyeMessage && (
                  <Box w={340}>
                    <IvrAudioFileInput
                      size="xs"
                      audioFile={endOfChainAudio}
                      disabled={false}
                      uploading={goodbyeAudio.uploading}
                      uploadPromptSuffix="the goodbye message"
                      emptyDescription="Played after the last rung, on every option"
                      onFileChange={goodbyeAudio.handleFileChange}
                      onRemove={() => setEndOfChainAudio(undefined)}
                    />
                  </Box>
                )}
                <Text size="sm" c="dimmed">
                  The missed-call row is created either way.
                </Text>
              </Group>
            </Paper>
          </Stack>

          {/* Below the mappings, not above (BAR-2624): the table is what an admin came here
              to edit, and the tester is what they use to check the edit. It reads the chains
              in `keyMappings` rather than what is saved, so an unsaved change shows up. */}
          <Stack gap="sm" mt="md">
            <Title order={3}>Call routing tester</Title>
            <IVRRouteTester
              menuLabel={pageTitle}
              keyMappings={keyMappings}
              hoursSets={hoursSets}
              languageSwitchKey={languageSwitchKey}
            />
          </Stack>
        </ScrollArea>

        <Group justify="flex-end">
          <Button variant="default" radius="xl" component={Link} to="/ivr-configuration">
            Cancel
          </Button>
          <Button color="blue" radius="xl" loading={saving} disabled={uploading} onClick={() => void handleSave()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
